import React, { useState, useEffect, useMemo } from "react";
import { Box, Paper, Tabs, Tab, CircularProgress, Typography } from "@mui/material";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from "chart.js";
import { getBulkUsers } from "../../../Action/api";
import PersonDetailDialog from "./PersonDetailDialog";
import EditPersonDialog from "./EditPersonDialog";
import PeopleCards from "./components/PeopleCards";
import DepartmentDistribution from "./components/DepartmentDistribution";
import GradeDistribution from "./components/GradeDistribution";
import LevelDistribution from "./components/LevelDistribution";
import DepartmentTableDialog from "./components/DepartmentTableDialog";
import CategoryTableDialog from "./components/CategoryTableDialog";
import "./styles-employees.css";
import "./styles.css";

// Register ChartJS components
ChartJS.register(
  ArcElement,
  ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
);

const People = () => {
  const [people, setPeople] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [editPerson, setEditPerson] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [chartTab, setChartTab] = useState(0);

  // State for department table dialog
  const [showDepartmentTable, setShowDepartmentTable] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedDepartmentColor, setSelectedDepartmentColor] = useState("#228B22");
  const [departmentPeople, setDepartmentPeople] = useState([]);

  // State for category table dialog (grade/level)
  const [showCategoryTable, setShowCategoryTable] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryPeople, setCategoryPeople] = useState([]);

  useEffect(() => {
    const fetchPeople = async () => {
      try {
        setIsLoading(true);
        const result = await getBulkUsers();
        console.log("Full API Response:", result);

        if (result && result.length > 0) {
          // Map the API response to match the expected structure
          const mappedData = result.map((person) => ({
            id: person.uId,
            staff_id: person.uId,
            uId: person.uId,
            name: person.staffFullName,
            role_name: person.roleName !== "NONE" ? person.roleName : "Not assigned",
            roleId: person.roleId,
            level_id: person.levelId,
            level: person.levelName !== "NONE" ? person.levelName : "Not assigned",
            level_name: person.levelName,
            grade_id: person.grade,
            grade: person.grade,
            dep_id: person.depId,
            dep_name: person.depName !== "NONE" ? person.depName : "Not assigned",
            res_country: person.nationality !== "NONE" ? person.nationality : "Not specified",
            per_email: person.personalEmail !== "NONE" ? person.personalEmail : person.userId,
            login_id: person.userId,
            profile_image: null,
            gender: person.gender,
            dateOfBirth: person.dateOfBirth !== "NONE" ? person.dateOfBirth : null,
            maritalStatus: person.maritalStatus,
            contactNumber: person.contactNumber,
            governmentId: person.governmentId,
            bloodGroup: person.bloodGroup,
            currentAddress: person.currentAddress,
            permanentAddress: person.permanentAddress,
            desId: person.desId,
            desName: person.desName,
            empTypeId: person.empTypeId,
            empTypeName: person.empTypeName,
            joiningDate: person.joiningDate,
            workLocation: person.workLocation,
            probationPeriod: person.probationPeriod,
            reportingMngId: person.reportingMngId,
            reportingManagerName: person.reportingManagerName,
            hobbies: person.hobbies,
            passion: person.yourPassion,
            shortTermGoal: person.shortTermGoal,
            longTermGoal: person.longTermGoal,
            primaryLanguage: person.primaryLanguage,
            knownLanguage: person.knownLanguage,
            allocatedAnnualLeave: person.allocatedAnnualLeave || 0,
            allocatedSickLeave: person.allocatedSickLeave || 0,
            allocatedEmergencyLeave: person.allocatedEmergencyLeave || 0,
            allocatedUnpaidLeave: person.allocatedUnpaidLeave || 0,
            allocatedMaternityLeave: person.allocatedMaternityLeave || 0,
            usedAnnualLeave: person.usedAnnualLeave || 0,
            usedSickLeave: person.usedSickLeave || 0,
            usedEmergencyLeave: person.usedEmergencyLeave || 0,
            createdAt: person.createdAt,
            updatedAt: person.updatedAt,
          }));
          console.log("Mapped People data:", mappedData);
          setPeople(mappedData);
        } else {
          console.warn("No data in response");
          setPeople([]);
        }
      } catch (error) {
        console.error("Error fetching people:", error);
        setPeople([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPeople();
  }, []);

  // Calculate department counts and prepare chart data
  const departmentStats = useMemo(() => {
    const counts = {};
    const nameMap = {};
    const idByName = {};

    people.forEach((person) => {
      const deptName = person.dep_name || "Unassigned";
      counts[deptName] = (counts[deptName] || 0) + 1;
      nameMap[deptName] = deptName;
      if (person.dep_id) {
        idByName[deptName] = person.dep_id;
      }
    });

    const sortedDepts = Object.keys(counts).sort(
      (a, b) => counts[b] - counts[a]
    );

    const chartLabels = sortedDepts;
    const chartData = sortedDepts.map((dept) => counts[dept]);

    const generateColors = (count) => {
      const colors = [
        "rgba(34, 139, 34, 0.8)",
        "rgba(54, 162, 235, 0.8)",
        "rgba(255, 99, 132, 0.8)",
        "rgba(255, 206, 86, 0.8)",
        "rgba(153, 102, 255, 0.8)",
        "rgba(255, 159, 64, 0.8)",
        "rgba(75, 192, 192, 0.8)",
        "rgba(201, 203, 207, 0.8)",
        "rgba(83, 102, 255, 0.8)",
        "rgba(255, 99, 255, 0.8)",
      ];

      const result = [];
      for (let i = 0; i < count; i++) {
        result.push(colors[i % colors.length]);
      }
      return result;
    };

    const backgroundColors = generateColors(chartLabels.length);

    const getDepartmentColor = (deptName) => {
      const index = sortedDepts.indexOf(deptName);
      return backgroundColors[index] || "#228B22";
    };

    return {
      counts,
      sortedDepts,
      deptNames: nameMap,
      idByName,
      chartData: {
        labels: chartLabels,
        datasets: [
          {
            data: chartData,
            backgroundColor: backgroundColors,
            borderColor: backgroundColors.map((color) =>
              color.replace("0.8", "1")
            ),
            borderWidth: 1,
          },
        ],
      },
      barChartData: {
        labels: chartLabels,
        datasets: [
          {
            label: "Number of Staff",
            data: chartData,
            backgroundColor: backgroundColors,
            borderColor: backgroundColors.map((color) =>
              color.replace("0.8", "1")
            ),
            borderWidth: 1,
          },
        ],
      },
      totalStaff: people.length,
      getDepartmentColor,
    };
  }, [people]);

  // Calculate grade distribution stats
  const gradeStats = useMemo(() => {
    const counts = {};
    const allGrades = [
      "G1", "G2", "G3", "G4", "G5", "G6", "G7", "G8", "G9", "G10", "Unassigned",
    ];

    allGrades.forEach((grade) => {
      counts[grade] = 0;
    });

    people.forEach((person) => {
      const grade = person.grade_id || "Unassigned";
      counts[grade] = (counts[grade] || 0) + 1;
    });

    const sortedGrades = allGrades.filter(
      (grade) => counts[grade] > 0 || allGrades.includes(grade)
    );

    const generateColors = () => {
      const colorMap = {
        G1: "rgba(54, 128, 229, 0.8)",
        G2: "rgba(54, 128, 229, 0.8)",
        G3: "rgba(54, 128, 229, 0.8)",
        G4: "rgba(60, 179, 113, 0.8)",
        G5: "rgba(60, 179, 113, 0.8)",
        G6: "rgba(60, 179, 113, 0.8)",
        G7: "rgba(255, 165, 0, 0.8)",
        G8: "rgba(255, 165, 0, 0.8)",
        G9: "rgba(255, 165, 0, 0.8)",
        G10: "rgba(255, 140, 0, 0.8)",
        Unassigned: "rgba(199, 199, 199, 0.8)",
      };
      return sortedGrades.map((grade) => colorMap[grade]);
    };

    const backgroundColors = generateColors();
    const chartLabels = sortedGrades;
    const chartData = sortedGrades.map((grade) => counts[grade]);

    const gradedStaffCount = Object.entries(counts)
      .filter(([key]) => key !== "Unassigned")
      .reduce((sum, [_, count]) => sum + count, 0);

    const getGradeColor = (grade) => {
      if (grade.startsWith("G") && parseInt(grade.substring(1)) <= 3) {
        return "#3680e5";
      } else if (grade.startsWith("G") && parseInt(grade.substring(1)) <= 6) {
        return "#3CB371";
      } else if (grade.startsWith("G") && parseInt(grade.substring(1)) <= 9) {
        return "#FFA500";
      } else if (grade === "G10") {
        return "#FF8C00";
      }
      return "#c7c7c7";
    };

    return {
      counts,
      sortedGrades,
      chartData: {
        labels: chartLabels,
        datasets: [
          {
            data: chartData,
            backgroundColor: backgroundColors,
            borderColor: backgroundColors.map((color) =>
              color.replace("0.8", "1")
            ),
            borderWidth: 1,
          },
        ],
      },
      barChartData: {
        labels: chartLabels,
        datasets: [
          {
            label: "Number of Staff",
            data: chartData,
            backgroundColor: backgroundColors,
            borderColor: backgroundColors.map((color) =>
              color.replace("0.8", "1")
            ),
            borderWidth: 1,
          },
        ],
      },
      gradedStaffCount,
      getGradeColor,
    };
  }, [people]);

  // Calculate level distribution stats
  const levelStats = useMemo(() => {
    const counts = {};

    people.forEach((person) => {
      const level = person.level || "Unassigned";
      counts[level] = (counts[level] || 0) + 1;
    });

    const sortedLevels = Object.keys(counts).sort(
      (a, b) => counts[b] - counts[a]
    );

    const generateColors = (count) => {
      const baseColors = [
        "rgba(156, 39, 176, 0.8)",
        "rgba(103, 58, 183, 0.8)",
        "rgba(63, 81, 181, 0.8)",
        "rgba(33, 150, 243, 0.8)",
        "rgba(3, 169, 244, 0.8)",
        "rgba(0, 188, 212, 0.8)",
        "rgba(0, 150, 136, 0.8)",
        "rgba(76, 175, 80, 0.8)",
        "rgba(139, 195, 74, 0.8)",
        "rgba(205, 220, 57, 0.8)",
      ];
      const result = [];
      for (let i = 0; i < count; i++) {
        result.push(baseColors[i % baseColors.length]);
      }
      return result;
    };

    const chartLabels = sortedLevels;
    const chartData = sortedLevels.map((level) => counts[level]);
    const backgroundColors = generateColors(chartLabels.length);

    const getLevelColor = (level) => {
      const index = sortedLevels.indexOf(level);
      return backgroundColors[index] || "rgba(156, 39, 176, 0.8)";
    };

    return {
      counts,
      sortedLevels,
      chartData: {
        labels: chartLabels,
        datasets: [
          {
            data: chartData,
            backgroundColor: backgroundColors,
            borderColor: backgroundColors.map((color) =>
              color.replace("0.8", "1")
            ),
            borderWidth: 1,
          },
        ],
      },
      barChartData: {
        labels: chartLabels,
        datasets: [
          {
            label: "Number of Staff",
            data: chartData,
            backgroundColor: backgroundColors,
            borderColor: backgroundColors.map((color) =>
              color.replace("0.8", "1")
            ),
            borderWidth: 1,
          },
        ],
      },
      getLevelColor,
    };
  }, [people]);

  // Event handlers
  const handleBarClick = (event, elements) => {
    if (elements.length > 0) {
      const { index } = elements[0];
      const deptName = departmentStats.barChartData.labels[index];
      setSelectedDepartment(deptName);
      setSelectedDepartmentColor(departmentStats.getDepartmentColor(deptName));
      const deptPeople = people.filter(
        (person) => person.dep_name === deptName
      );
      setDepartmentPeople(deptPeople);
      setShowDepartmentTable(true);
    }
  };

  const handleGradeBarClick = (event, elements) => {
    if (elements.length > 0) {
      const { index } = elements[0];
      const gradeId = gradeStats.barChartData.labels[index];
      setSelectedCategory(gradeId);
      const filteredPeople = people.filter(
        (person) => person.grade_id === gradeId
      );
      setCategoryPeople(filteredPeople);
      setShowCategoryTable(true);
    }
  };

  const handleLevelBarClick = (event, elements) => {
    if (elements.length > 0) {
      const { index } = elements[0];
      const levelName = levelStats.barChartData.labels[index];
      setSelectedCategory(levelName);
      const filteredPeople = people.filter(
        (person) => person.level === levelName
      );
      setCategoryPeople(filteredPeople);
      setShowCategoryTable(true);
    }
  };

  const handleDepartmentClick = (dept) => {
    setSelectedDepartment(dept);
    setSelectedDepartmentColor(departmentStats.getDepartmentColor(dept));
    const deptPeople = people.filter((person) => person.dep_name === dept);
    setDepartmentPeople(deptPeople);
    setShowDepartmentTable(true);
  };

  const handleGradeClick = (gradeId) => {
    setSelectedCategory(gradeId);
    const filteredPeople = people.filter(
      (person) => person.grade_id === gradeId
    );
    setCategoryPeople(filteredPeople);
    setShowCategoryTable(true);
  };

  const handleLevelClick = (levelName) => {
    setSelectedCategory(levelName);
    const filteredPeople = people.filter(
      (person) => person.level === levelName
    );
    setCategoryPeople(filteredPeople);
    setShowCategoryTable(true);
  };

  const handleCloseDepartmentTable = () => {
    setShowDepartmentTable(false);
    setSelectedDepartment(null);
    setDepartmentPeople([]);
  };

  const handleCloseCategoryTable = () => {
    setShowCategoryTable(false);
    setSelectedCategory(null);
    setCategoryPeople([]);
  };

  const handleTabChange = (event, newValue) => {
    setChartTab(newValue);
  };

  const handleClose = () => {
    setSelectedPerson(null);
  };

  const handleEdit = (person) => {
    setEditPerson(person);
    setSelectedPerson(null);
  };

  const handleEditClose = () => {
    setEditPerson(null);
  };

  const handleEditSuccess = () => {
    setEditPerson(null);
    // Refresh data
    const fetchPeople = async () => {
      try {
        const result = await getBulkUsers();
        if (result && result.length > 0) {
          // Map the API response to match the expected structure
          const mappedData = result.map((person) => ({
            id: person.uId,
            staff_id: person.uId,
            uId: person.uId,
            name: person.staffFullName,
            role_name: person.roleName !== "NONE" ? person.roleName : "Not assigned",
            roleId: person.roleId,
            level_id: person.levelId,
            level: person.levelName !== "NONE" ? person.levelName : "Not assigned",
            level_name: person.levelName,
            grade_id: person.grade,
            grade: person.grade,
            dep_id: person.depId,
            dep_name: person.depName !== "NONE" ? person.depName : "Not assigned",
            res_country: person.nationality !== "NONE" ? person.nationality : "Not specified",
            per_email: person.personalEmail !== "NONE" ? person.personalEmail : person.userId,
            login_id: person.userId,
            profile_image: null,
            gender: person.gender,
            dateOfBirth: person.dateOfBirth !== "NONE" ? person.dateOfBirth : null,
            maritalStatus: person.maritalStatus,
            contactNumber: person.contactNumber,
            governmentId: person.governmentId,
            bloodGroup: person.bloodGroup,
            currentAddress: person.currentAddress,
            permanentAddress: person.permanentAddress,
            desId: person.desId,
            desName: person.desName,
            empTypeId: person.empTypeId,
            empTypeName: person.empTypeName,
            joiningDate: person.joiningDate,
            workLocation: person.workLocation,
            probationPeriod: person.probationPeriod,
            reportingMngId: person.reportingMngId,
            reportingManagerName: person.reportingManagerName,
            hobbies: person.hobbies,
            passion: person.yourPassion,
            shortTermGoal: person.shortTermGoal,
            longTermGoal: person.longTermGoal,
            primaryLanguage: person.primaryLanguage,
            knownLanguage: person.knownLanguage,
            allocatedAnnualLeave: person.allocatedAnnualLeave || 0,
            allocatedSickLeave: person.allocatedSickLeave || 0,
            allocatedEmergencyLeave: person.allocatedEmergencyLeave || 0,
            allocatedUnpaidLeave: person.allocatedUnpaidLeave || 0,
            allocatedMaternityLeave: person.allocatedMaternityLeave || 0,
            usedAnnualLeave: person.usedAnnualLeave || 0,
            usedSickLeave: person.usedSickLeave || 0,
            usedEmergencyLeave: person.usedEmergencyLeave || 0,
            createdAt: person.createdAt,
            updatedAt: person.updatedAt,
          }));
          setPeople(mappedData);
        }
      } catch (error) {
        console.error("Error refreshing people:", error);
      }
    };
    fetchPeople();
  };

  return (
    <div className="people-container">
      <h1>Staff Directory</h1>

      {/* Chart Tabs */}
      <Paper elevation={1} sx={{ mb: 2, borderRadius: "12px" }}>
        <Tabs
          value={chartTab}
          onChange={handleTabChange}
          variant="fullWidth"
          textColor="primary"
          TabIndicatorProps={{
            style: {
              backgroundColor: "#228B22",
            },
          }}
          sx={{
            "& .MuiTab-root": {
              color: "#777",
              fontWeight: 500,
              "&.Mui-selected": {
                color: "#228B22",
                fontWeight: 700,
              },
            },
          }}
        >
          <Tab label="People Cards" />
          <Tab label="Department Distribution" />
          <Tab label="Grade Distribution" />
          <Tab label="Level Distribution" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {chartTab === 0 && (
        <PeopleCards
          people={people}
          search={search}
          setSearch={setSearch}
          onPersonClick={setSelectedPerson}
          isLoading={isLoading}
        />
      )}

      {chartTab === 1 && (
        <DepartmentDistribution
          departmentStats={departmentStats}
          people={people}
          onBarClick={handleBarClick}
          onDepartmentClick={handleDepartmentClick}
        />
      )}

      {chartTab === 2 && (
        <GradeDistribution
          gradeStats={gradeStats}
          onBarClick={handleGradeBarClick}
          onGradeClick={handleGradeClick}
        />
      )}

      {chartTab === 3 && (
        <LevelDistribution
          levelStats={levelStats}
          onBarClick={handleLevelBarClick}
          onLevelClick={handleLevelClick}
        />
      )}

      {/* Loading indicator */}
      {isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            my: 4,
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <CircularProgress
            color="success"
            size={40}
            thickness={4}
            sx={{ mb: 2 }}
          />
          <Typography variant="h6" sx={{ color: "text.secondary" }}>
            Loading people data...
          </Typography>
        </Box>
      )}

      {/* Dialogs */}
      <PersonDetailDialog
        selectedPerson={selectedPerson}
        open={Boolean(selectedPerson)}
        onClose={handleClose}
        onEdit={handleEdit}
      />

      <EditPersonDialog
        personData={editPerson}
        open={Boolean(editPerson)}
        onClose={handleEditClose}
        onSuccess={handleEditSuccess}
      />

      <DepartmentTableDialog
        open={showDepartmentTable}
        onClose={handleCloseDepartmentTable}
        selectedDepartment={selectedDepartment}
        departmentPeople={departmentPeople}
        selectedDepartmentColor={selectedDepartmentColor}
        onPersonClick={setSelectedPerson}
      />

      <CategoryTableDialog
        open={showCategoryTable}
        onClose={handleCloseCategoryTable}
        selectedCategory={selectedCategory}
        categoryPeople={categoryPeople}
        chartTab={chartTab}
        gradeStats={gradeStats}
        levelStats={levelStats}
        onPersonClick={setSelectedPerson}
      />
    </div>
  );
};

export default People;
